import { BsBatteryCharging } from "react-icons/bs";
import { TabButtonType } from "@habity/types";
import { MdCastle, MdFamilyRestroom, MdNature, MdWaves, MdOutlineElderly, MdForest, MdApartment } from "react-icons/md";
import { RiHomeHeartFill } from "react-icons/ri";
import { GiFarmTractor, GiModernCity } from "react-icons/gi";
import { FaSwimmingPool, FaHorse, FaUniversity } from "react-icons/fa";

const CURATION_OPTIONS = {
  "Alla": {
    icon: null,
    key: {},
  } as Omit<TabButtonType, "title">,
  "Underhand": {
    icon: MdNature,
    key: {
      statuses: ["premarket"],
    },
  } as Omit<TabButtonType, "title">,
  // Village/small town properties
  "Lantligt": {
    icon: MdNature,
    key: {
      categories: ["house", "farm"],
      features: ["garden", "rustic"],
      plot_size_min: 2500,
      build_year_max: 1950,
    },
  } as Omit<TabButtonType, "title">,

  // Sea villas
  "Sjövillor": {
    icon: MdWaves,
    key: {
      categories: ["house"],
      features: ["sea_view"],
    },
  } as Omit<TabButtonType, "title">,

  // City center properties
  "Centralt": {
    icon: GiModernCity,
    key: {
      categories: ["apartment"],
      nearby: ["restaurant", "cafe", "bar"],
      features: ["commute_friendly"],
    },
  } as Omit<TabButtonType, "title">,

  // Waterfront properties
  "Havsnära": {
    icon: MdWaves,
    key: {
      categories: ["house"],
      features: ["near_sea"],
    },
  } as Omit<TabButtonType, "title">,

  // Energy-efficient homes
  "Energihus": {
    icon: BsBatteryCharging,
    key: {
      categories: ["house"],
      features: ["solar_panels", "geothermal_heating"],
      build_year_min: 2000,
    },
  } as Omit<TabButtonType, "title">,

  // Senior living options
  "Senior": {
    icon: MdOutlineElderly,
    key: {
      categories: ["apartment"],
      nearby: ["pharmacy", "supermarket"],
      features: ["elevator"],
    },
  } as Omit<TabButtonType, "title">,

  // Student apartments near universities
  "Student": {
    icon: FaUniversity,
    key: {
      categories: ["apartment"],
      nearby: ["university"],
      price_min: 1000000,
      price_max: 3000000,
      rooms_max: 2,
    },
  } as Omit<TabButtonType, "title">,

  // Properties in scenic natural settings
  "Naturskönt": {
    icon: MdForest,
    key: {
      categories: ["house", "farm", "cottage"],
      features: ["scenic_nature_area", "mountain_view", "garden"],
      plot_size_min: 2500,
    },
  } as Omit<TabButtonType, "title">,

  // Classic apartments with high ceilings
  "Sekelskifte": {
    icon: MdCastle,
    key: {
      features: ["high_ceiling", "rustic"],
      build_year_max: 1940,
      price_min: 5000000,
      categories: ["apartment"],
    },
  } as Omit<TabButtonType, "title">,

  // Family-friendly homes near schools
  "Barnfamilj": {
    icon: MdFamilyRestroom,
    key: {
      categories: ["house"],
      nearby: ["kindergarten", "school"],
      rooms_min: 3,
    },
  } as Omit<TabButtonType, "title">,

  // New construction
  "Nyproduktion": {
    icon: MdApartment,
    key: {
      categories: ["new_construction"],
    },
  } as Omit<TabButtonType, "title">,

  // Commercial
  "Kommersiellt": {
    icon: MdApartment,
    key: {
      categories: ["commercial"],
    },
  } as Omit<TabButtonType, "title">,

  // Properties for horse enthusiasts
  "Hästgård": {
    icon: FaHorse,
    key: {
      categories: ["farm", "house"],
      plot_size_min: 20000, // 1 hectare minimum
    },
  } as Omit<TabButtonType, "title">,

  // Cozy homes
  "Hygge": {
    icon: RiHomeHeartFill,
    key: {
      categories: ["house", "cottage"],
      features: ["fireplace", "rustic"],
      rooms_min: 2,
      rooms_max: 5,
      build_year_max: 1910,
    },
  } as Omit<TabButtonType, "title">,

  // Farms and rural properties
  "Gård": {
    icon: GiFarmTractor,
    key: {
      categories: ["house", "farm"],
      plot_size_min: 3500,
    },
  } as Omit<TabButtonType, "title">,

  // Properties with pools
  "Med pool": {
    icon: FaSwimmingPool,
    key: {
      categories: ["house", "farm", "cottage"],
      features: ["pool"],
      price_min: 4000000,
    },
  } as Omit<TabButtonType, "title">,

  // Properties needing renovation
  // "Renoveringsobjekt": {
  //   title: "",
  //   icon: BsTools,
  //   key: {
  //     categories: ["house", "apartment"],
  //     build_year_min: 1970,
  //     build_year_max: 1990,
  //     price_max: 3000000,
  //   },
  // },

  // Mountain/ski properties
  // "Fjällnära": {
  //   icon: TbMountain,
  //   key: {
  //     features: ["mountain_view"],
  //     categories: ["house", "cottage"],
  //   },
  // },

  // Luxury apartments
  "Lyxvåning": {
    icon: MdApartment,
    key: {
      categories: ["apartment"],
      features: ["high_ceiling", "reputed_area", "rustic"],
      price_min: 8000000,
      rooms_min: 3,
      living_space_min: 120,
    },
  } as Omit<TabButtonType, "title">,
};


// const SIGNED_IN_CURATION_OPTIONS = {
//   "Underhand": {
//     icon: MdNature,
//     key: {
//       statuses: ["premarket"],
//     },
//   } as Omit<TabButtonType, "title">,
// };

export const getCuratedButtons = (signedIn = false): TabButtonType[] => {
  const generalButtons: TabButtonType[] = [];

  for (const [title, obj] of Object.entries(CURATION_OPTIONS)) {
    generalButtons.push({
      title,
      ...obj,
    });
  }

  // const signedInButtons: TabButtonType[] = [];

  // for (const [title, obj] of Object.entries(SIGNED_IN_CURATION_OPTIONS)) {
  //   signedInButtons.push({
  //     title,
  //     ...obj,
  //   });
  // }

  // const buttons = signedIn
  //   ? [...signedInButtons, ...generalButtons]
  //   : generalButtons

  return generalButtons;
};

const PROJECT_CURATION_OPTIONS = {
  "Alla": {
    icon: MdApartment,
    key: {},
  } as Omit<TabButtonType, "title">,
  "Planerat": {
    icon: MdNature,
    key: {
      statuses: ["soon_for_sale"],
    },
  } as Omit<TabButtonType, "title">,
  "Försäljning pågår": {
    icon: MdApartment,
    key: {
      statuses: ["for_sale"],
    },
  } as Omit<TabButtonType, "title">,
  "Slutsålt": {
    icon: MdCastle,
    key: {
      statuses: ["sold"],
    },
  } as Omit<TabButtonType, "title">,
};

export const getProjectCuratedButtons = (): TabButtonType[] => {
  const buttons: TabButtonType[] = [];

  for (const [title, obj] of Object.entries(PROJECT_CURATION_OPTIONS)) {
    buttons.push({
      title,
      ...obj,
    });
  }

  return buttons;
};
